import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Container, Form, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Award, Gift, Layers, Plus, Save, Settings, Star, Tag, Trash2, TrendingUp } from "react-feather";
import Breadcrumb from "../common/breadcrumb";
import Loader from "../../shared/Loader";
import MultiSelectField from "../common/MultiSelectField";
import PillTabs from "../common/PillTabs";
import { errorToast } from "../../shared/Toast";
import { C, reportingGlobalCSS, selectStyles, tbl } from "../reporting/reportingStyles";
import { RCard, TablePagination } from "../reporting/ReportingShared";
import {
    Cols, Field, SaveBar, Section, SwitchField,
    hintStyle, inputStyle, primaryBtnStyle, secondaryBtnStyle, smallInputStyle, withDisabled,
} from "../common/FormKit";
import PointsExpirySection, {
    pointsExpiryFormFrom,
    pointsExpiryPayload,
    validatePointsExpiryForm,
} from "../loyalty/PointsExpirySection";
import {
    createLoyalty, getCategoryListing, getLoyaltyById, getMerchantListing, getProductListing, updateLoyalty,
} from "../../utils/apiCalls";

/**
 * Create or edit a loyalty programme (stamps or points).
 *
 * ONE component serving /merchant/add-loyalty and /merchant/edit-loyalty/:loyaltyId. It replaces
 * the add-loyalty.js / edit-loyalty.js pair, two near-copies of ~1,400 lines that had already
 * drifted (the edit page lost the Arabic name on every save for a while). `params.loyaltyId`
 * decides the mode; the payloads are exactly what those two pages sent.
 *
 * Layout: the FormKit look (titled sections, uppercase labels, sticky save bar) split into
 * PillTabs, like membership-type-form.js.
 */

/** Two columns of sections on a wide screen, one on a narrow one. */
const sectionGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))",
    gap: 16,
    alignItems: "start",
};

/** react-select, themed to match the inputs, with its menu above the section borders. */
const selectProps = {
    styles: selectStyles,
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
};

const iconBtnStyle = {
    border: "none",
    background: "none",
    color: C.coral,
    cursor: "pointer",
    padding: 4,
    display: "inline-flex",
};

/** Field defaults mirror the API's column defaults, so a straight save changes nothing. */
const EMPTY_FORM = {
    merchantId: "",
    name: "",
    arabicName: "",
    loyaltyType: "",
    eligibleLoyaltyAmount: "",
    basePointsPerSar: "",
    useTiersForPercentage: false,
    percentageTiers: [],
    rewardOnStampsCount: "",
    useStampTiers: false,
    stampTiers: [],
    products: [],
    categories: [],
    redeemableProductPercentage: "",
    redeemProducts: [],
    isActive: true,
    voucherExpiryDays: 30,
    voucherExpiryPolicy: "forfeit",
    rewardIncludesModifiers: false,
    // Points are counted on the full basket unless an admin turns this on.
    earnOnNetOfDiscounts: false,
};

/** Where a redeemable product may be taken. The API enforces it on vouchers and direct rewards alike. */
const REWARD_TYPES = [
    { value: "both", label: "App & store" },
    { value: "app", label: "App only" },
    { value: "pos", label: "Store only" },
];

/** Inputs that must not go negative: a negative entry is reset to 0, as the old pages did. */
const NON_NEGATIVE = ["eligibleLoyaltyAmount", "basePointsPerSar", "rewardOnStampsCount", "redeemableProductPercentage"];

/**
 * What the two rates actually cost, as a percentage of spend.
 *
 * The earn rate and the redeem rate are independent numbers, and their RATIO is the only
 * figure that costs money: earn 10 per SAR and give a SAR back for 200 points and you are
 * paying 5%. Derived, never stored — there is no third field to keep in step.
 */
export const cashbackPercent = (pointsPerSar, redeemPointsPerSar) => {
    const earn = Number(pointsPerSar);
    const redeem = Number(redeemPointsPerSar);
    if (!Number.isFinite(earn) || !Number.isFinite(redeem) || earn <= 0 || redeem <= 0) return null;
    const pct = (earn / redeem) * 100;
    // Two decimals: 0.05 points per SAR against 50 is 0.1%, and a whole number would print 0%.
    return Number.isInteger(pct) ? String(pct) : pct.toFixed(2).replace(/\.?0+$/, "");
};

/**
 * Mirrors validatePercentageTiers in the API so the admin sees the same rule the server applies.
 * Messages name the ROW and the on-screen label, not the JSON path. Null when valid.
 */
export const validatePercentageTiersLocal = (tiers) => {
    if (!Array.isArray(tiers) || tiers.length === 0) return null;
    const seenNames = new Set();
    const seenPoints = new Set();
    for (let i = 0; i < tiers.length; i++) {
        const t = tiers[i];
        const row = i + 1;
        const name = String(t.tierName || "").trim();
        if (!name) return `Row ${row}: give the tier a name.`;
        if (seenNames.has(name.toLowerCase())) return `Two tiers are both called "${name}" — names must be unique.`;
        seenNames.add(name.toLowerCase());

        const earn = Number(t.pointsPerSar);
        if (!Number.isFinite(earn) || earn < 0) return `Row ${row}: earn must be a number of points, 0 or more.`;

        const pts = Number(t.eligibilityPoints);
        if (!Number.isInteger(pts) || pts < 0) return `Row ${row}: "unlocks at" must be a whole number of points, 0 or more.`;
        if (seenPoints.has(pts)) return `Two tiers both unlock at ${pts} points — each tier needs its own threshold.`;
        seenPoints.add(pts);

        const redeem = Number(t.redeemPointsPerSar);
        if (!Number.isFinite(redeem) || redeem < 0) return `Row ${row}: redeem must be a number of points, 0 or more.`;
    }
    const base = tiers.find((t) => Number(t.eligibilityPoints) === 0);
    if (!base) return "One tier must unlock at 0 points — that is the tier every customer starts on.";
    if (!(Number(base.redeemPointsPerSar) > 0)) {
        return "The tier that unlocks at 0 points must say how many points buy SAR 1, otherwise nobody can redeem.";
    }
    return null;
};

/** Form state from an API programme row. Numbers stay strings for the inputs. */
const formFromRow = (data) => ({
    ...EMPTY_FORM,
    merchantId: data.merchantId,
    name: data.name || "",
    arabicName: data.arabicName || "",
    loyaltyType: data.loyaltyType || "",
    eligibleLoyaltyAmount: data.eligibleLoyaltyAmount || "",
    // New field first, old one as a fallback: a programme not re-saved since the convention
    // changed still only has the percentage, and dividing it by 100 is the same rate.
    basePointsPerSar: data.basePointsPerSar ?? (data.basePercentage != null ? Number(data.basePercentage) / 100 : ""),
    useTiersForPercentage: data.useTiersForPercentage || false,
    percentageTiers: (data.percentageTiers || []).map((t) => ({
        tierName: t?.tierName ?? "",
        pointsPerSar: t?.pointsPerSar ?? (t?.percentage != null ? Number(t.percentage) / 100 : ""),
        eligibilityPoints: t?.eligibilityPoints ?? "",
        redeemPointsPerSar: t?.redeemPointsPerSar ?? (Number(t?.pointToCurrency) > 0 ? 1 / Number(t.pointToCurrency) : ""),
    })),
    rewardOnStampsCount: data.rewardOnStampsCount || "",
    // Type-aware, so a stray stampTiers JSON on a percentage row can't flip the toggle.
    useStampTiers: data.loyaltyType === "stamps" && data.stampTiers?.length > 0,
    stampTiers: data.stampTiers || [],
    products: (data.eligibleProducts || []).map((p) => p.id),
    categories: (data.eligibleCategories || []).map((c) => c.id),
    redeemableProductPercentage: data.redeemableProductPercentage || "",
    redeemProducts: (data.redeemableProductEntries || []).map((entry) => ({
        productId: entry?.productId,
        rewardType: entry?.rewardType || "both",
    })),
    isActive: data.isActive,
    voucherExpiryDays: data.voucherExpiryDays || 30,
    voucherExpiryPolicy: data.voucherExpiryPolicy || "forfeit",
    rewardIncludesModifiers: data.rewardIncludesModifiers || false,
    earnOnNetOfDiscounts: data.earnOnNetOfDiscounts || false,
});

/** The first ten products plus whatever is selected, and a "Show More..." entry opening the picker. */
const dropdownOptions = (allProducts, selectedIds) => {
    if (!allProducts || allProducts.length === 0) return [];
    const selected = allProducts.filter((p) => selectedIds.includes(p.id));
    const unique = Array.from(new Map([...allProducts.slice(0, 10), ...selected].map((p) => [p.id, p])).values());
    const options = unique.map((p) => ({ value: p.id, label: p.title }));
    if (allProducts.length > unique.length) options.push({ value: "SHOW_MORE", label: "Show More..." });
    return options;
};

const LoyaltyForm = () => {
    const navigate = useNavigate();
    const { loyaltyId } = useParams();
    const [searchParams] = useSearchParams();
    const isEdit = !!loyaltyId;

    const userType = localStorage.getItem("userType");
    const loggedInMerchantId = localStorage.getItem("MerchantId");
    const isMerchant = userType === "merchant";
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    // Edit: the programme's merchant (from the link). Add: the header's merchant.
    const routeMerchantId = isEdit
        ? searchParams.get("merchantId")
        : isMerchant ? loggedInMerchantId : reduxSelectedMerchant;

    const [tab, setTab] = useState("programme");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [savedForm, setSavedForm] = useState(EMPTY_FORM);
    const [expiry, setExpiry] = useState(() => pointsExpiryFormFrom());
    const [savedExpiry, setSavedExpiry] = useState(() => pointsExpiryFormFrom());
    const [expiryWasEnabled, setExpiryWasEnabled] = useState(false);

    const [merchants, setMerchants] = useState([]);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);

    // Product picker (the "Show More..." modal).
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerType, setPickerType] = useState(null); // "eligible" | "redeemable"
    const [pickerSearch, setPickerSearch] = useState("");
    const [pickerCategory, setPickerCategory] = useState("");
    const [pickerPage, setPickerPage] = useState(1);
    const [pickerLimit, setPickerLimit] = useState(10);
    const [pickerProducts, setPickerProducts] = useState([]);
    const [pickerTotal, setPickerTotal] = useState(0);
    const [pickerLoading, setPickerLoading] = useState(false);
    const [pickerSelected, setPickerSelected] = useState(new Set());

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
    const setNumber = (key, value) => set({ [key]: NON_NEGATIVE.includes(key) && Number.parseFloat(value) < 0 ? "0" : value });

    const isPoints = form.loyaltyType === "percentage";
    const isStamps = form.loyaltyType === "stamps";
    const merchantId = isMerchant ? loggedInMerchantId : form.merchantId;

    // ── Load ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isEdit || !loyaltyId || !routeMerchantId) return;
        setLoading(true);
        getLoyaltyById(loyaltyId, routeMerchantId)
            .then((data) => {
                if (!data) return;
                const loaded = formFromRow(data);
                setForm(loaded);
                setSavedForm(loaded);
                const loadedExpiry = pointsExpiryFormFrom(data);
                setExpiry(loadedExpiry);
                setSavedExpiry(loadedExpiry);
                // Balances are only dated while the programme is live, so "already on" from the
                // customers' side means both enabled and active.
                setExpiryWasEnabled(!!data.pointsExpiryEnabled && !!data.isActive);
            })
            .finally(() => setLoading(false));
    }, [isEdit, loyaltyId, routeMerchantId]);

    // Add: the programme belongs to the merchant chosen in the header.
    useEffect(() => {
        if (isEdit) return;
        const id = routeMerchantId && routeMerchantId !== "all" ? routeMerchantId : "";
        setForm((prev) => ({ ...prev, merchantId: id }));
        setSavedForm((prev) => ({ ...prev, merchantId: id }));
    }, [isEdit, routeMerchantId]);

    useEffect(() => {
        if (!merchantId) return;
        getProductListing(merchantId).then((rows) => setProducts(rows || []));
        getCategoryListing(merchantId).then((rows) => setCategories(rows || []));
    }, [merchantId]);

    useEffect(() => {
        if (!isMerchant) getMerchantListing().then((rows) => setMerchants(rows || []));
    }, [isMerchant]);

    const merchantName = useMemo(
        () => merchants?.find((m) => String(m.id) === String(form.merchantId))?.name || "",
        [merchants, form.merchantId]
    );

    // ── Tier rows ───────────────────────────────────────────────────────────
    const setPercentageTier = (index, key, value) =>
        set({ percentageTiers: form.percentageTiers.map((t, i) => (i === index ? { ...t, [key]: value } : t)) });
    // The first row is the mandatory base tier: what every customer resolves to at 0 points.
    const addPercentageTier = () =>
        set({
            percentageTiers: [
                ...form.percentageTiers,
                form.percentageTiers.length === 0
                    ? { tierName: "Base", pointsPerSar: "", eligibilityPoints: "0", redeemPointsPerSar: "" }
                    : { tierName: "", pointsPerSar: "", eligibilityPoints: "", redeemPointsPerSar: "" },
            ],
        });
    const removePercentageTier = (index) => set({ percentageTiers: form.percentageTiers.filter((_, i) => i !== index) });

    const setStampTier = (index, key, value) =>
        set({ stampTiers: form.stampTiers.map((t, i) => (i === index ? { ...t, [key]: value } : t)) });
    const addStampTier = () => set({ stampTiers: [...form.stampTiers, { minAmount: "", stamps: "" }] });
    const removeStampTier = (index) => set({ stampTiers: form.stampTiers.filter((_, i) => i !== index) });

    // ── Products ────────────────────────────────────────────────────────────
    const redeemableIds = useMemo(() => form.redeemProducts.map((rp) => rp.productId), [form.redeemProducts]);
    const eligibleOptions = useMemo(() => dropdownOptions(products, form.products), [products, form.products]);
    const redeemableOptions = useMemo(() => dropdownOptions(products, redeemableIds), [products, redeemableIds]);
    const categoryOptions = useMemo(() => (categories || []).map((c) => ({ value: c.id, label: c.name })), [categories]);

    /** Selected ids → redeem rows, keeping the rewardType of rows that stay selected. */
    const withRedeemSelection = (selectedIds) => {
        const kept = form.redeemProducts.filter((p) => selectedIds.includes(p.productId));
        selectedIds.forEach((id) => {
            if (!kept.some((p) => p.productId === id)) kept.push({ productId: id, rewardType: "both" });
        });
        return kept;
    };

    const openPicker = (type, selectedIds) => {
        setPickerType(type);
        setPickerSelected(new Set(selectedIds));
        setPickerSearch("");
        setPickerCategory("");
        setPickerPage(1);
        setPickerOpen(true);
    };

    const onEligibleChange = (selected) => {
        if ((selected || []).some((o) => o.value === "SHOW_MORE")) return openPicker("eligible", form.products);
        set({ products: (selected || []).map((o) => o.value) });
    };

    const onRedeemableChange = (selected) => {
        if ((selected || []).some((o) => o.value === "SHOW_MORE")) return openPicker("redeemable", redeemableIds);
        set({ redeemProducts: withRedeemSelection((selected || []).map((o) => o.value)) });
    };

    /** A product picked in the modal may not be in the first page of `products`: add it so its name shows. */
    const rememberProducts = (list) =>
        setProducts((current) => {
            const known = new Set(current.map((p) => p.id));
            const fresh = list.filter((p) => !known.has(p.id));
            return fresh.length ? [...current, ...fresh] : current;
        });

    const togglePicked = (product) => {
        setPickerSelected((prev) => {
            const next = new Set(prev);
            if (next.has(product.id)) next.delete(product.id);
            else {
                next.add(product.id);
                rememberProducts([product]);
            }
            return next;
        });
    };

    const togglePickedPage = (checked) => {
        setPickerSelected((prev) => {
            const next = new Set(prev);
            pickerProducts.forEach((p) => (checked ? next.add(p.id) : next.delete(p.id)));
            return next;
        });
        if (checked) rememberProducts(pickerProducts);
    };

    const savePicker = () => {
        const ids = Array.from(pickerSelected);
        if (pickerType === "eligible") set({ products: ids });
        else if (pickerType === "redeemable") set({ redeemProducts: withRedeemSelection(ids) });
        setPickerOpen(false);
    };

    useEffect(() => {
        if (!pickerOpen) return;
        let cancelled = false;
        setPickerLoading(true);
        getProductListing(merchantId, { search: pickerSearch, categoryId: pickerCategory, isActive: true }, pickerPage, pickerLimit)
            .then((res) => {
                if (cancelled) return;
                setPickerProducts(res?.data || []);
                setPickerTotal(res?.totalCount || 0);
            })
            .finally(() => !cancelled && setPickerLoading(false));
        return () => { cancelled = true; };
    }, [pickerOpen, pickerPage, pickerLimit, pickerSearch, pickerCategory, merchantId]);

    // ── Validation, dirty state, save ───────────────────────────────────────
    const tierError = isPoints ? validatePercentageTiersLocal(form.percentageTiers) : null;
    const expiryError = isPoints ? validatePointsExpiryForm(expiry) : null;
    const dirty = JSON.stringify({ form, expiry }) !== JSON.stringify({ form: savedForm, expiry: savedExpiry });

    const buildPayload = () => {
        const payload = {
            ...(isEdit ? { id: loyaltyId } : {}),
            merchantId: Number(merchantId),
            name: form.name,
            arabicName: form.arabicName,
            loyaltyType: form.loyaltyType,
            isActive: form.isActive,
            eligibleLoyaltyAmount: Number.parseFloat(form.eligibleLoyaltyAmount) || 0,
            redeemableProducts: form.redeemProducts || [],
            redeemableProductPercentage: Number.parseFloat(form.redeemableProductPercentage) || 0,
            rewardIncludesModifiers: form.rewardIncludesModifiers,
            // Shared by both programme types — see the note on the switch.
            earnOnNetOfDiscounts: !!form.earnOnNetOfDiscounts,
            // Both programme types issue vouchers: stamps from a reward, points from spent points.
            voucherExpiryDays: Number.parseInt(form.voucherExpiryDays, 10) || 30,
            voucherExpiryPolicy: form.voucherExpiryPolicy,
        };

        if (isStamps) {
            payload.eligibleProductIds = form.products || [];
            payload.eligibleCategoryIds = form.categories || [];
            payload.rewardOnStampsCount = Number.parseInt(form.rewardOnStampsCount, 10) || 0;
            payload.useStampTiers = form.useStampTiers;
            if (form.useStampTiers) {
                payload.stampTiers = form.stampTiers.map((t) => ({ minAmount: Number(t.minAmount) || 0, stamps: Number(t.stamps) || 0 }));
            }
        } else if (isPoints) {
            Object.assign(payload, pointsExpiryPayload(expiry));
            payload.basePointsPerSar = Number.parseFloat(form.basePointsPerSar) || 0;
            payload.useTiersForPercentage = form.useTiersForPercentage;
            // Always sent for points, so removing every row clears the ladder.
            payload.percentageTiers = (form.percentageTiers || []).map((t) => ({
                tierName: String(t.tierName || "").trim(),
                pointsPerSar: Number(t.pointsPerSar) || 0,
                eligibilityPoints: Number.parseInt(t.eligibilityPoints, 10) || 0,
                redeemPointsPerSar: Number(t.redeemPointsPerSar) || 0,
            }));
        }
        return payload;
    };

    /** First problem, with the tab it is on, or null. */
    const firstProblem = () => {
        if (!form.name.trim()) return { tab: "programme", message: "Please fill in the name field." };
        if (!Number(merchantId)) return { tab: "programme", message: "Please select a merchant." };
        if (!form.loyaltyType) return { tab: "programme", message: "Choose a programme type." };
        if (tierError) return { tab: "earning", message: tierError };
        if (expiryError) return { tab: "expiry", message: expiryError };
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const problem = firstProblem();
        if (problem) {
            setTab(problem.tab);
            errorToast(problem.message);
            return;
        }
        setSaving(true);
        try {
            const payload = buildPayload();
            const response = isEdit ? await updateLoyalty(payload) : await createLoyalty(payload);
            if (response) navigate("/merchant/loyalty");
        } catch (error) {
            console.error(`Error ${isEdit ? "updating" : "adding"} loyalty program:`, error);
        } finally {
            setSaving(false);
        }
    };

    const discard = () => {
        setForm(savedForm);
        setExpiry(savedExpiry);
    };

    const tabs = [
        { key: "programme", label: "Programme" },
        { key: "earning", label: "Earning", alert: !!tierError },
        { key: "rewards", label: "Rewards & vouchers", badge: form.redeemProducts.length },
        ...(isPoints ? [{ key: "expiry", label: "Points expiry", alert: !!expiryError }] : []),
    ];
    // Switching an unsaved new programme to stamps hides the expiry tab: never leave it selected.
    const activeTab = tabs.some((t) => t.key === tab) ? tab : "programme";

    const typeLabel = isPoints ? "Points" : isStamps ? "Stamps" : null;
    const saveDisabled = saving || !Number(merchantId) || (isEdit && !dirty);

    if (loading) return <Loader />;

    return (
        <Fragment>
            <Breadcrumb title={isEdit ? "Edit Loyalty" : "Add Loyalty"} parent="Loyalty" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">
                    {/* ── Header ──────────────────────────────────────── */}
                    <RCard style={{ marginBottom: 16 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                            <div
                                style={{
                                    width: 48, height: 48, borderRadius: 12, flexShrink: 0,
                                    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
                                }}
                            >
                                <Award size={22} color={C.white} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 18, fontWeight: 800, color: C.text, letterSpacing: -0.3 }}>
                                    {isEdit ? form.name || "Loyalty programme" : "New loyalty programme"}
                                    {typeLabel && (
                                        <span
                                            style={{
                                                marginLeft: 10, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                                                background: C.coralLight, color: C.coral, verticalAlign: "middle",
                                            }}
                                        >
                                            {typeLabel}
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: 12, color: C.textMed, marginTop: 4 }}>
                                    {isEdit
                                        ? "Changes apply to orders placed after you save — past orders keep the terms they were placed under."
                                        : "Only one programme is active per merchant: activating this one turns the current one off."}
                                    {!isMerchant && merchantName && <> · Merchant: <strong>{merchantName}</strong></>}
                                </div>
                            </div>
                        </div>
                    </RCard>

                    {!Number(merchantId) && (
                        <div style={{ padding: 16, marginBottom: 16, borderRadius: 12, background: C.orangeLight, color: C.text, fontSize: 13 }}>
                            Select a merchant from the header before creating a programme.
                        </div>
                    )}

                    <Form className="loyalty-form" onSubmit={handleSubmit}>
                        <PillTabs ariaLabel="Programme sections" value={activeTab} onChange={setTab} tabs={tabs} />

                        {/* ── Programme ───────────────────────────────── */}
                        {activeTab === "programme" && (
                            <div style={sectionGrid}>
                                <Section icon={Award} title="Programme" subtitle="How the programme is named in the app and on the Wallet card.">
                                    <Field label="Program name" required>
                                        <input
                                            type="text"
                                            value={form.name}
                                            onChange={(e) => set({ name: e.target.value })}
                                            placeholder="e.g. Coffee Rewards"
                                            style={inputStyle}
                                        />
                                    </Field>
                                    <Field label="Program name (Arabic)" hint="Shown to customers using the app in Arabic.">
                                        <input
                                            type="text"
                                            value={form.arabicName}
                                            onChange={(e) => set({ arabicName: e.target.value })}
                                            placeholder="اسم برنامج الولاء"
                                            dir="rtl"
                                            style={inputStyle}
                                        />
                                    </Field>
                                </Section>

                                <Section icon={Settings} title="Type & status">
                                    <Field
                                        label="Loyalty type"
                                        required
                                        hint={isEdit ? "The type of an existing programme cannot change: balances are counted differently." : undefined}
                                    >
                                        <select
                                            value={form.loyaltyType}
                                            onChange={(e) => set({ loyaltyType: e.target.value })}
                                            disabled={isEdit}
                                            style={withDisabled(inputStyle, isEdit)}
                                        >
                                            <option value="">Select type</option>
                                            <option value="percentage">Points (percentage of spend)</option>
                                            <option value="stamps">Stamps</option>
                                        </select>
                                    </Field>
                                    <SwitchField
                                        label="Active"
                                        checked={form.isActive}
                                        onChange={(v) => set({ isActive: v })}
                                        hint="Customers earn and redeem only on the active programme."
                                    />
                                </Section>
                            </div>
                        )}

                        {/* ── Earning ─────────────────────────────────── */}
                        {activeTab === "earning" && !typeLabel && (
                            <div style={{ ...hintStyle, padding: 16 }}>Choose a programme type on the Programme tab first.</div>
                        )}
                        {activeTab === "earning" && typeLabel && (
                            <div style={sectionGrid}>
                                <Section icon={TrendingUp} title="How customers earn">
                                    {isPoints && (
                                        <Field
                                            label="Earn rate (points per SAR)"
                                            hint="Used when tiers are off. With tiers on, each tier's own rate applies."
                                        >
                                            <input
                                                type="number"
                                                min="0"
                                                placeholder="e.g. 10"
                                                value={form.basePointsPerSar}
                                                onChange={(e) => setNumber("basePointsPerSar", e.target.value)}
                                                style={inputStyle}
                                            />
                                        </Field>
                                    )}
                                    {isStamps && (
                                        <Field label="Reward on stamps count" hint="Stamps a customer collects to earn one reward.">
                                            <input
                                                type="number"
                                                min="0"
                                                placeholder="e.g. 7"
                                                value={form.rewardOnStampsCount}
                                                onChange={(e) => setNumber("rewardOnStampsCount", e.target.value)}
                                                style={inputStyle}
                                            />
                                        </Field>
                                    )}
                                    <Field label="Eligible loyalty amount (SAR)" hint="Minimum eligible spend before an order earns anything.">
                                        <input
                                            type="number"
                                            min="0"
                                            placeholder="0"
                                            value={form.eligibleLoyaltyAmount}
                                            onChange={(e) => setNumber("eligibleLoyaltyAmount", e.target.value)}
                                            style={inputStyle}
                                        />
                                    </Field>
                                    {/*
                                      Shown for both types on purpose: it governs the earning BASE, which
                                      prepareLoyaltyForOrder computes the same way for points and stamps. The flag
                                      is frozen into each order's loyaltySnapshot, so placed orders never move.
                                    */}
                                    <SwitchField
                                        label="Earn after discounts"
                                        checked={form.earnOnNetOfDiscounts}
                                        onChange={(v) => set({ earnOnNetOfDiscounts: v })}
                                        hint="Off: earning is counted on the full basket. On: discounts and coupons come off first, so a discounted order earns less. Existing orders are unaffected."
                                    />
                                </Section>

                                {isStamps && (
                                    <Section icon={Star} title="Stamp tiers" subtitle="Give more stamps for bigger orders.">
                                        <SwitchField
                                            label="Use tiers for stamps"
                                            checked={form.useStampTiers}
                                            onChange={(v) => set({ useStampTiers: v })}
                                        />
                                        {form.useStampTiers && (
                                            <>
                                                <table style={tbl.table}>
                                                    <thead>
                                                        <tr>
                                                            <th style={tbl.th}>Minimum amount (SAR)</th>
                                                            <th style={tbl.th}>Stamps</th>
                                                            <th style={{ ...tbl.th, width: 48 }} />
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {form.stampTiers.map((tier, index) => (
                                                            <tr key={index}>
                                                                <td style={tbl.td}>
                                                                    <input
                                                                        type="number"
                                                                        aria-label={`Stamp tier ${index + 1} minimum amount`}
                                                                        value={tier.minAmount}
                                                                        onChange={(e) => setStampTier(index, "minAmount", e.target.value)}
                                                                        style={smallInputStyle}
                                                                    />
                                                                </td>
                                                                <td style={tbl.td}>
                                                                    <input
                                                                        type="number"
                                                                        aria-label={`Stamp tier ${index + 1} stamps`}
                                                                        value={tier.stamps}
                                                                        onChange={(e) => setStampTier(index, "stamps", e.target.value)}
                                                                        style={smallInputStyle}
                                                                    />
                                                                </td>
                                                                <td style={tbl.td}>
                                                                    <button type="button" aria-label={`Remove stamp tier ${index + 1}`} onClick={() => removeStampTier(index)} style={iconBtnStyle}>
                                                                        <Trash2 size={15} />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                                <div>
                                                    <button type="button" onClick={addStampTier} style={secondaryBtnStyle}>
                                                        <Plus size={14} /> Add tier
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </Section>
                                )}

                                {isStamps && (
                                    <Section icon={Layers} title="What earns stamps" subtitle="Leave both empty to let every product earn.">
                                        <Field label="Products">
                                            <MultiSelectField
                                                {...selectProps}
                                                options={eligibleOptions}
                                                value={eligibleOptions.filter((o) => form.products.includes(o.value))}
                                                onChange={onEligibleChange}
                                                placeholder="Search & select products..."
                                                isDisabled={!Number(merchantId)}
                                            />
                                        </Field>
                                        <Field label="Categories">
                                            <MultiSelectField
                                                {...selectProps}
                                                options={categoryOptions}
                                                value={categoryOptions.filter((o) => form.categories.includes(o.value))}
                                                onChange={(sel) => set({ categories: (sel || []).map((o) => o.value) })}
                                                placeholder="Search & select categories..."
                                                isDisabled={!Number(merchantId)}
                                            />
                                        </Field>
                                    </Section>
                                )}
                            </div>
                        )}

                        {activeTab === "earning" && isPoints && (
                            <div style={{ marginTop: 16 }}>
                                <Section
                                    icon={Layers}
                                    title="Tier ladder"
                                    subtitle="Customers move up as their lifetime points grow. A tier change applies immediately."
                                    aside={
                                        <SwitchField
                                            label="Use tiers"
                                            checked={form.useTiersForPercentage}
                                            onChange={(v) => set({ useTiersForPercentage: v })}
                                        />
                                    }
                                >
                                    <div style={{ overflowX: "auto" }}>
                                        <table style={tbl.table}>
                                            <thead>
                                                <tr>
                                                    {/* Named for what they DO, not for the stored field: "eligibility points"
                                                        read as points you get, when it is the lifetime total a customer must pass. */}
                                                    <th style={tbl.th}>Tier name</th>
                                                    <th style={tbl.th}>Earn (points per SAR)</th>
                                                    <th style={tbl.th}>Unlocks at (lifetime points)</th>
                                                    <th style={tbl.th}>Redeem (points for SAR 1)</th>
                                                    <th style={tbl.th}>Worth (of spend)</th>
                                                    <th style={{ ...tbl.th, width: 48 }} />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {form.percentageTiers.map((tier, index) => {
                                                    const worth = cashbackPercent(tier.pointsPerSar, tier.redeemPointsPerSar);
                                                    return (
                                                        <tr key={index}>
                                                            <td style={tbl.td}>
                                                                <input
                                                                    type="text"
                                                                    aria-label={`Tier ${index + 1} name`}
                                                                    placeholder="e.g. Bronze"
                                                                    value={tier.tierName}
                                                                    onChange={(e) => setPercentageTier(index, "tierName", e.target.value)}
                                                                    style={smallInputStyle}
                                                                />
                                                            </td>
                                                            <td style={tbl.td}>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    aria-label={`Tier ${index + 1} earn`}
                                                                    placeholder="e.g. 10"
                                                                    value={tier.pointsPerSar}
                                                                    onChange={(e) => setPercentageTier(index, "pointsPerSar", e.target.value)}
                                                                    style={smallInputStyle}
                                                                />
                                                            </td>
                                                            <td style={tbl.td}>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    aria-label={`Tier ${index + 1} unlocks at`}
                                                                    placeholder="e.g. 500"
                                                                    value={tier.eligibilityPoints}
                                                                    onChange={(e) => setPercentageTier(index, "eligibilityPoints", e.target.value)}
                                                                    style={smallInputStyle}
                                                                />
                                                            </td>
                                                            <td style={tbl.td}>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    aria-label={`Tier ${index + 1} redeem`}
                                                                    placeholder="e.g. 200"
                                                                    value={tier.redeemPointsPerSar}
                                                                    onChange={(e) => setPercentageTier(index, "redeemPointsPerSar", e.target.value)}
                                                                    style={smallInputStyle}
                                                                />
                                                            </td>
                                                            <td style={{ ...tbl.td, fontWeight: 700, color: C.text }}>
                                                                {worth ? `${worth}%` : <span style={{ color: C.textLight }}>—</span>}
                                                            </td>
                                                            <td style={tbl.td}>
                                                                <button type="button" aria-label={`Remove tier ${index + 1}`} onClick={() => removePercentageTier(index)} style={iconBtnStyle}>
                                                                    <Trash2 size={15} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div>
                                        <button type="button" onClick={addPercentageTier} style={secondaryBtnStyle}>
                                            <Plus size={14} /> Add tier
                                        </button>
                                    </div>
                                    {tierError && <span style={{ ...hintStyle, color: C.orange }} role="alert">{tierError}</span>}
                                    <span style={hintStyle}>
                                        <strong>Earn</strong> is points per SAR spent: 10 turns a SAR 100 order into 1,000 points.{" "}
                                        <strong>Redeem</strong> is how many points buy SAR 1 back.{" "}
                                        <strong>Worth</strong> is the two divided — the discount you are actually giving. Raising Earn
                                        from 10 to 50 without touching Redeem takes a 5% programme to 25%.{" "}
                                        <strong>Unlocks at</strong> is the customer&apos;s lifetime total; the tier at 0 applies to everyone
                                        who has not reached a higher one.
                                    </span>
                                </Section>
                            </div>
                        )}

                        {/* ── Rewards & vouchers ──────────────────────── */}
                        {activeTab === "rewards" && (
                            <>
                                <div style={sectionGrid}>
                                    <Section icon={Gift} title="Reward value">
                                        <Field label="Redeemable product percentage">
                                            <input
                                                type="number"
                                                min="0"
                                                placeholder="e.g. 100"
                                                value={form.redeemableProductPercentage}
                                                onChange={(e) => setNumber("redeemableProductPercentage", e.target.value)}
                                                style={inputStyle}
                                            />
                                        </Field>
                                        <SwitchField
                                            label="Reward includes modifiers"
                                            checked={form.rewardIncludesModifiers}
                                            onChange={(v) => set({ rewardIncludesModifiers: v })}
                                            hint="On: the reward also covers the item's modifiers. Off: the customer pays for them."
                                        />
                                    </Section>

                                    <Section
                                        icon={Tag}
                                        title="Vouchers"
                                        subtitle={
                                            isPoints
                                                ? "Applies to vouchers customers claim with points. Refund returns the points spent when a voucher expires unused."
                                                : "Applies to vouchers customers create from a stamps reward."
                                        }
                                    >
                                        <Cols min={160}>
                                            <Field label="Voucher expiry days">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="365"
                                                    placeholder="30"
                                                    value={form.voucherExpiryDays}
                                                    onChange={(e) => set({ voucherExpiryDays: e.target.value })}
                                                    style={inputStyle}
                                                />
                                            </Field>
                                            <Field label="Voucher expiry policy">
                                                <select
                                                    value={form.voucherExpiryPolicy}
                                                    onChange={(e) => set({ voucherExpiryPolicy: e.target.value })}
                                                    style={inputStyle}
                                                >
                                                    <option value="forfeit">Forfeit</option>
                                                    <option value="refund">Refund</option>
                                                </select>
                                            </Field>
                                        </Cols>
                                    </Section>
                                </div>

                                <div style={{ marginTop: 16 }}>
                                    <Section
                                        icon={Gift}
                                        title="Redeemable products"
                                        subtitle="Where each reward can be taken: in the app cart, at the store counter, or both. The app and the POS both follow this."
                                    >
                                        <MultiSelectField
                                            {...selectProps}
                                            options={redeemableOptions}
                                            value={redeemableOptions.filter((o) => redeemableIds.includes(o.value))}
                                            onChange={onRedeemableChange}
                                            placeholder="Search & select products..."
                                            isDisabled={!Number(merchantId)}
                                        />
                                        {form.redeemProducts.length > 0 && (
                                            <table style={tbl.table}>
                                                <thead>
                                                    <tr>
                                                        <th style={tbl.th}>Product</th>
                                                        <th style={tbl.th}>Redeemable in</th>
                                                        <th style={{ ...tbl.th, width: 48 }} />
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {form.redeemProducts.map((item) => {
                                                        const product = products?.find((p) => String(p?.id) === String(item.productId));
                                                        const inactive = product?.is_active === false;
                                                        return (
                                                            <tr key={item.productId}>
                                                                <td
                                                                    style={{
                                                                        ...tbl.td,
                                                                        textDecoration: inactive ? "line-through" : "none",
                                                                        color: inactive ? C.textLight : C.text,
                                                                    }}
                                                                >
                                                                    {product?.title || `#${item.productId}`}
                                                                </td>
                                                                <td style={tbl.td}>
                                                                    <select
                                                                        aria-label={`Where ${product?.title || item.productId} can be redeemed`}
                                                                        value={item.rewardType}
                                                                        onChange={(e) =>
                                                                            set({
                                                                                redeemProducts: form.redeemProducts.map((p) =>
                                                                                    p.productId === item.productId ? { ...p, rewardType: e.target.value } : p
                                                                                ),
                                                                            })
                                                                        }
                                                                        style={smallInputStyle}
                                                                    >
                                                                        {REWARD_TYPES.map((r) => (
                                                                            <option key={r.value} value={r.value}>{r.label}</option>
                                                                        ))}
                                                                    </select>
                                                                </td>
                                                                <td style={tbl.td}>
                                                                    <button
                                                                        type="button"
                                                                        aria-label={`Remove ${product?.title || item.productId}`}
                                                                        onClick={() => set({ redeemProducts: form.redeemProducts.filter((p) => p.productId !== item.productId) })}
                                                                        style={iconBtnStyle}
                                                                    >
                                                                        <Trash2 size={15} />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        )}
                                    </Section>
                                </div>
                            </>
                        )}

                        {/* ── Points expiry ───────────────────────────── */}
                        {activeTab === "expiry" && isPoints && (
                            <PointsExpirySection value={expiry} onChange={setExpiry} wasEnabled={expiryWasEnabled} />
                        )}

                        <SaveBar dirty={dirty}>
                            <button type="button" onClick={() => navigate("/merchant/loyalty")} style={secondaryBtnStyle}>
                                Cancel
                            </button>
                            <button type="button" onClick={discard} disabled={!dirty || saving} style={withDisabled(secondaryBtnStyle, !dirty || saving)}>
                                Discard
                            </button>
                            <button type="submit" disabled={saveDisabled} style={withDisabled(primaryBtnStyle, saveDisabled)}>
                                <Save size={14} /> {saving ? "Saving…" : isEdit ? "Save changes" : "Create programme"}
                            </button>
                        </SaveBar>
                    </Form>
                </div>
            </Container>

            {/* ── Product picker ("Show More...") ─────────────────── */}
            <Modal isOpen={pickerOpen} toggle={() => setPickerOpen(false)} size="lg">
                <ModalHeader toggle={() => setPickerOpen(false)}>
                    Select {pickerType === "eligible" ? "eligible" : "redeemable"} products
                </ModalHeader>
                <ModalBody>
                    <Cols>
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={pickerSearch}
                            onChange={(e) => { setPickerSearch(e.target.value); setPickerPage(1); }}
                            style={inputStyle}
                        />
                        <select
                            value={pickerCategory}
                            onChange={(e) => { setPickerCategory(e.target.value); setPickerPage(1); }}
                            style={inputStyle}
                        >
                            <option value="">All categories</option>
                            {(categories || []).map((cat) => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                    </Cols>
                    <div style={{ maxHeight: 400, overflowY: "auto", marginTop: 12 }}>
                        {pickerLoading ? <Loader /> : (
                            <table style={tbl.table}>
                                <thead>
                                    <tr>
                                        <th style={{ ...tbl.th, width: 48 }}>
                                            <input
                                                type="checkbox"
                                                aria-label="Select all on this page"
                                                onChange={(e) => togglePickedPage(e.target.checked)}
                                                checked={pickerProducts.length > 0 && pickerProducts.every((p) => pickerSelected.has(p.id))}
                                                disabled={pickerProducts.length === 0}
                                            />
                                        </th>
                                        <th style={tbl.th}>Product</th>
                                        <th style={tbl.th}>Category</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pickerProducts.map((product) => (
                                        <tr key={product.id}>
                                            <td style={tbl.td}>
                                                <input
                                                    type="checkbox"
                                                    aria-label={`Select ${product.title}`}
                                                    checked={pickerSelected.has(product.id)}
                                                    onChange={() => togglePicked(product)}
                                                />
                                            </td>
                                            <td style={tbl.td}>{product.title}</td>
                                            <td style={{ ...tbl.td, color: C.textMed }}>{product.Category?.name || product.CategoryId}</td>
                                        </tr>
                                    ))}
                                    {pickerProducts.length === 0 && (
                                        <tr>
                                            <td colSpan={3} style={{ ...tbl.td, textAlign: "center", color: C.textLight }}>No products found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <TablePagination
                        total={pickerTotal}
                        page={pickerPage}
                        limit={pickerLimit}
                        onPageChange={setPickerPage}
                        onLimitChange={(n) => { setPickerLimit(n); setPickerPage(1); }}
                    />
                </ModalBody>
                <ModalFooter>
                    <button type="button" onClick={() => setPickerOpen(false)} style={secondaryBtnStyle}>Cancel</button>
                    <button type="button" onClick={savePicker} style={primaryBtnStyle}>Save selection</button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default LoyaltyForm;
